"use client"

import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  FileText,
  MessageSquare,
  Save,
  RotateCcw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Edit3,
  X,
  Check
} from "lucide-react"
import { fetchPrompts, updatePrompt, resetPrompt } from "../../../lib/api/api"
import { formatDate } from "../../../shared/utils/format"

interface Prompt {
  id: string
  key: string
  name: string
  description?: string
  category: string
  content: string
  default_content: string
  is_active: boolean
  updated_at?: string
}

interface CategoryGroup {
  [category: string]: Prompt[]
}

// Labels/descriptions are i18n keys (platform.prompts.categories.<category>); this map only
// carries the icon, which doesn't go through t().
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  document_processing: FileText,
  chat_system: MessageSquare
}

export default function PromptsSettings() {
  const { t } = useTranslation()
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null)
  const [editedContent, setEditedContent] = useState<string>("")
  const [savingPrompt, setSavingPrompt] = useState<string | null>(null)
  const [resettingPrompt, setResettingPrompt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const loadPrompts = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await fetchPrompts()
      setPrompts(response.data)
      // Expand all categories by default
      const categories = new Set<string>(response.data.map((p: Prompt) => p.category))
      setExpandedCategories(categories)
    } catch (error) {
      console.error("Failed to load prompts:", error)
      setError(t('platform.prompts.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadPrompts()
  }, [loadPrompts])

  const groupPromptsByCategory = (prompts: Prompt[]): CategoryGroup => {
    return prompts.reduce((acc, prompt) => {
      if (!acc[prompt.category]) {
        acc[prompt.category] = []
      }
      acc[prompt.category].push(prompt)
      return acc
    }, {} as CategoryGroup)
  }

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(category)) {
      newExpanded.delete(category)
    } else {
      newExpanded.add(category)
    }
    setExpandedCategories(newExpanded)
  }

  const startEditing = (prompt: Prompt) => {
    setEditingPrompt(prompt.key)
    setEditedContent(prompt.content)
  }

  const cancelEditing = () => {
    setEditingPrompt(null)
    setEditedContent("")
  }

  const savePrompt = async (promptKey: string) => {
    try {
      setSavingPrompt(promptKey)
      setError(null)
      
      await updatePrompt(promptKey, { content: editedContent })
      
      // Update local state
      setPrompts(prompts.map(p => 
        p.key === promptKey 
          ? { ...p, content: editedContent, updated_at: new Date().toISOString() }
          : p
      ))
      
      setEditingPrompt(null)
      setSuccessMessage(t('platform.prompts.saveSuccess', { name: prompts.find(p => p.key === promptKey)?.name }))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error: any) {
      setError(error.response?.data?.detail || t('platform.prompts.saveFailed'))
    } finally {
      setSavingPrompt(null)
    }
  }

  const handleResetPrompt = async (prompt: Prompt) => {
    if (!window.confirm(t('platform.prompts.confirmReset', { name: prompt.name }))) {
      return
    }

    try {
      setResettingPrompt(prompt.key)
      setError(null)
      
      await resetPrompt(prompt.key)
      
      // Update local state
      setPrompts(prompts.map(p => 
        p.key === prompt.key 
          ? { ...p, content: p.default_content, updated_at: new Date().toISOString() }
          : p
      ))
      
      if (editingPrompt === prompt.key) {
        cancelEditing()
      }
      
      setSuccessMessage(t('platform.prompts.resetSuccess', { name: prompt.name }))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error: any) {
      setError(error.response?.data?.detail || t('platform.prompts.resetFailed'))
    } finally {
      setResettingPrompt(null)
    }
  }

  const isPromptModified = (prompt: Prompt) => {
    return prompt.content !== prompt.default_content
  }

  const groupedPrompts = groupPromptsByCategory(prompts)

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <X className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <Check className="w-5 h-5 text-green-500 mr-2" />
          <span className="text-green-700">{successMessage}</span>
        </div>
      )}

      {/* Categories */}
      {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => {
        const categoryMeta = CATEGORY_ICONS[category]
          ? {
              icon: CATEGORY_ICONS[category],
              label: t(`platform.prompts.categories.${category}.label`),
              description: t(`platform.prompts.categories.${category}.description`)
            }
          : { icon: FileText, label: category, description: "" }
        const Icon = categoryMeta.icon
        const isExpanded = expandedCategories.has(category)

        return (
          <div key={category} className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Icon className="w-5 h-5 text-primary-600" />
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {categoryMeta.label}
                  </h3>
                  {categoryMeta.description && (
                    <p className="text-sm text-gray-500">{categoryMeta.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {t('platform.prompts.count', { count: categoryPrompts.length })}
                </span>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Prompts in Category */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                {categoryPrompts.map((prompt, index) => (
                  <div
                    key={prompt.key}
                    className={`p-6 ${
                      index < categoryPrompts.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    {/* Prompt Header */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="text-base font-medium text-gray-800">
                          {prompt.name}
                        </h4>
                        {prompt.description && (
                          <p className="text-sm text-gray-500 mt-1">{prompt.description}</p>
                        )}
                        {isPromptModified(prompt) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 mt-2">
                            {t('platform.prompts.modified')}
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {editingPrompt === prompt.key ? (
                          <>
                            <button
                              onClick={() => savePrompt(prompt.key)}
                              disabled={savingPrompt === prompt.key}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title={t('platform.prompts.save')}
                            >
                              {savingPrompt === prompt.key ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                              title={t('common.cancel')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(prompt)}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title={t('common.edit')}
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            {isPromptModified(prompt) && (
                              <button
                                onClick={() => handleResetPrompt(prompt)}
                                disabled={resettingPrompt === prompt.key}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                                title={t('platform.prompts.resetToDefault')}
                              >
                                {resettingPrompt === prompt.key ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Prompt Content */}
                    {editingPrompt === prompt.key ? (
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="w-full min-h-[200px] p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t('platform.prompts.contentPlaceholder')}
                      />
                    ) : (
                      <div className="relative">
                        <pre className="p-3 bg-gray-50 rounded-lg font-mono text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-[300px] overflow-y-auto">
                          {prompt.content}
                        </pre>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                      <span>{t('platform.prompts.key')} <code className="bg-gray-100 px-1 rounded">{prompt.key}</code></span>
                      {prompt.updated_at && (
                        <span>
                          {t('platform.prompts.lastUpdated', { date: formatDate(prompt.updated_at) })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}